import { useState, FormEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { useAuth, NewPasswordRequiredError } from './AuthContext';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SignInModal({ open, onClose }: Props) {
  const { signIn, completeNewPassword, newPasswordRequired } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setNewPassword('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      handleClose();
    } catch (err) {
      // Not a real failure - the form below switches to the new-password step.
      if (!(err instanceof NewPasswordRequiredError)) {
        setError(err instanceof Error ? err.message : 'Sign in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { bgcolor: 'background.paper' } } }}
    >
      {newPasswordRequired ? (
        <form onSubmit={handleNewPassword}>
          <DialogTitle sx={{ pt: 4, px: 4 }}>Set a New Password</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 4, pt: '8px !important' }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="info">
              This account was invited with a temporary password. Choose a new one to finish signing in.
            </Alert>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
              required
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disableElevation disabled={loading}>
              {loading ? 'Saving…' : 'Set Password'}
            </Button>
          </DialogActions>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ pt: 4, px: 4 }}>Sign In</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 4, pt: '8px !important' }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disableElevation disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
